import {
    appendUniqueAssignmentResourceEntries,
    focusFirstAssignmentResourceEntry,
    initializeAssignmentResourceRows,
    updateAssignmentResourceLiveRegion
} from './assignment_resource_list';

let assignmentResourcePaginationObserver = null;
let assignmentResourcePaginationRequest = null;

const paginationSelector = '[data-assignment-resource-pagination]';
const loadMoreSelector = '[data-assignment-resource-load-more]';

const disconnectPagination = () => {
    assignmentResourcePaginationObserver?.disconnect();
    assignmentResourcePaginationObserver = null;

    if (assignmentResourcePaginationRequest) {
        assignmentResourcePaginationRequest.abort();
        assignmentResourcePaginationRequest = null;
    }
};

const markListFullyLoaded = (list) => {
    list.dataset.listFullyLoaded = 'true';
};

const updateLoadingState = (pagination, loading) => {
    const button = pagination.querySelector(loadMoreSelector);
    const list = pagination.closest('#assignment_resource');

    pagination.dataset.loading = loading.toString();
    if (button) {
        button.disabled = loading;
        button.textContent = loading ? 'Loading assignments…' : 'Load more assignments';
    }
    list?.setAttribute('aria-busy', loading.toString());
};

const announce = (pagination, message) => {
    const status = pagination.querySelector('[data-assignment-resource-pagination-status]');
    updateAssignmentResourceLiveRegion(status, message);
};

const observePagination = (pagination, list) => {
    if (!('IntersectionObserver' in window) || !pagination.dataset.nextPageUrl) return;

    assignmentResourcePaginationObserver = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) loadNextPage(pagination, list);
    }, {
        root: list,
        rootMargin: '0px 0px 300px 0px'
    });
    assignmentResourcePaginationObserver.observe(pagination);
};

const loadNextPage = (pagination, list, options = {}) => {
    const nextPageUrl = pagination.dataset.nextPageUrl;
    if (!nextPageUrl) return null;
    if (pagination.dataset.loading === 'true') return assignmentResourcePaginationRequest;

    let loadFailed = false;
    updateLoadingState(pagination, true);
    assignmentResourcePaginationRequest = $.ajax({
        url: nextPageUrl,
        type: 'GET',
        dataType: 'json'
    }).done((response) => {
        if (!pagination.isConnected) return;

        const appendedEntries = appendUniqueAssignmentResourceEntries(list, pagination, response.items_html);
        const appendedCount = appendedEntries.length;
        pagination.dataset.nextPageUrl = response.next_page_url || '';
        pagination.dataset.totalCount = response.total_count || pagination.dataset.totalCount || '';
        const itemLabel = appendedCount === 1
            ? (pagination.dataset.itemLabelSingular || 'assignment')
            : (pagination.dataset.itemLabelPlural || 'assignments');
        announce(pagination, `${appendedCount} more ${itemLabel} loaded.`);
        if (!response.has_more) markListFullyLoaded(list);
        initializeAssignmentResourceRows(appendedEntries, {
            refreshSelection: appendedEntries.some((entry) => entry.querySelector('.group-check')),
            initializeSorting: list.classList.contains('assignment-sortable'),
            initializeFolders: true
        });
        if (options.moveFocus) focusFirstAssignmentResourceEntry(appendedEntries);

        const button = pagination.querySelector(loadMoreSelector);
        if (!response.has_more) {
            if (button) button.hidden = true;
            assignmentResourcePaginationObserver?.disconnect();
        } else if (assignmentResourcePaginationObserver) {
            assignmentResourcePaginationObserver.unobserve(pagination);
            assignmentResourcePaginationObserver.observe(pagination);
        }
    }).fail((_xhr, status) => {
        if (status === 'abort' || !pagination.isConnected) return;

        loadFailed = true;
        announce(pagination, 'Assignments could not be loaded. Use the retry button to try again.');
    }).always(() => {
        if (pagination.isConnected) {
            updateLoadingState(pagination, false);
            const button = pagination.querySelector(loadMoreSelector);
            if (loadFailed && button) button.textContent = 'Retry loading assignments';
        }
        assignmentResourcePaginationRequest = null;
    });

    return assignmentResourcePaginationRequest;
};

const paginationContext = () => {
    const list = document.getElementById('assignment_resource');
    const pagination = list?.querySelector(paginationSelector);

    return { list, pagination };
};

const requestCompletion = (request) => {
    if (!request) return Promise.resolve(false);

    return new Promise((resolve) => {
        request.done(() => resolve(true));
        request.fail(() => resolve(false));
    });
};

window.hasMoreAssignmentResourcePages = () => {
    const { pagination } = paginationContext();
    return Boolean(pagination?.dataset.nextPageUrl);
};

window.loadNextAssignmentResourcePage = (options = {}) => {
    const { list, pagination } = paginationContext();
    if (!list || !pagination) return Promise.resolve(false);

    return requestCompletion(loadNextPage(pagination, list, options));
};

window.initializeAssignmentResourcePagination = () => {
    disconnectPagination();

    const { list, pagination } = paginationContext();
    if (!list || !pagination) return;

    const button = pagination.querySelector(loadMoreSelector);
    if (button && button.dataset.assignmentResourcePaginationBound !== 'true') {
        button.dataset.assignmentResourcePaginationBound = 'true';
        button.addEventListener('click', () => loadNextPage(pagination, list, { moveFocus: true }));
    }
    observePagination(pagination, list);
};

document.addEventListener('turbolinks:load', window.initializeAssignmentResourcePagination);
document.addEventListener('turbolinks:before-cache', disconnectPagination);
